import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { useLocation } from './LocationContext'
import {
  getTrendsByCategory,
  loadTrendData,
} from '../components/trends/trendsRegistry'

const TrendsContext = createContext()

const initialState = {
  selectedCategory: 'education',
  trendsData: {}, // Object to hold data for multiple trend types
  loading: false,
  error: null,
}

function trendsReducer(state, action) {
  switch (action.type) {
    case 'SET_CATEGORY':
      return {
        ...state,
        selectedCategory: action.category,
        trendsData: {}, // Reset data when category changes
        error: null,
      }

    case 'SET_LOADING':
      return {
        ...state,
        loading: action.loading,
      }

    case 'SET_TREND_DATA':
      return {
        ...state,
        trendsData: {
          ...state.trendsData,
          [action.trendId]: action.data,
        },
        loading: false,
        error: null,
      }

    case 'SET_ERROR':
      return {
        ...state,
        error: action.error,
        loading: false,
      }

    default:
      return state
  }
}

export function TrendsProvider({ children }) {
  const [state, dispatch] = useReducer(trendsReducer, initialState)
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentLocationCode } = useLocation()

  // Initialize category from URL parameters only on mount
  useEffect(() => {
    const category = searchParams.get('category') || 'education'

    // Force education category for now (health is not ready)
    const finalCategory = category === 'health' ? 'education' : category

    if (finalCategory !== state.selectedCategory) {
      dispatch({ type: 'SET_CATEGORY', category: finalCategory })
    }
  }, []) // Only run on mount

  // Update URL parameters when category changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams)

    if (state.selectedCategory && state.selectedCategory !== 'education') {
      params.set('category', state.selectedCategory)
    } else {
      params.delete('category')
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true })
    }
  }, [state.selectedCategory, searchParams, setSearchParams])

  const setCategory = useCallback(category => {
    // Force education category for now (health is not ready)
    if (category === 'health') {
      category = 'education'
    }
    dispatch({ type: 'SET_CATEGORY', category })
  }, [])

  const loadTrendsData = useCallback(async () => {
    if (!state.selectedCategory || state.selectedCategory !== 'education')
      return

    const availableTrends = getTrendsByCategory(state.selectedCategory)
    if (!availableTrends || availableTrends.length === 0) return

    dispatch({ type: 'SET_LOADING', loading: true })

    try {
      // Load data for each trend type
      for (const trend of availableTrends) {
        const filters = {
          location_code: currentLocationCode,
        }

        const trendData = await loadTrendData(trend, filters)
        dispatch({
          type: 'SET_TREND_DATA',
          trendId: trend.id,
          data: trendData,
        })
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: error.message })
    }
  }, [state.selectedCategory, currentLocationCode])

  // Load data when category or location changes
  useEffect(() => {
    loadTrendsData()
  }, [loadTrendsData])

  const value = {
    ...state,
    setCategory,
    loadTrendsData,
  }

  return (
    <TrendsContext.Provider value={value}>{children}</TrendsContext.Provider>
  )
}

export function useTrends() {
  const context = useContext(TrendsContext)
  if (context === undefined) {
    throw new Error('useTrends must be used within a TrendsProvider')
  }
  return context
}

TrendsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
